import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Badge,
  FormGroup,
  Label,
} from 'reactstrap'
import { setContactExcluded, setContactOverride } from '../../actions/cspRenewalsActions'

// Mirrors the backend CSPRecipientEmails.Split contract for the normalized
// stored shape (bare addresses, comma-separated): split, trim, dedupe
// case-insensitively. Display-name forms are reduced to bare addresses by
// the server on save, so this doesn't need to handle them.
export const splitOverrideEmails = (raw) => {
  const seen = new Set()
  return (raw || '')
    .split(/[,;]/)
    .map((e) => e.trim())
    .filter((e) => {
      if (e.length === 0) return false
      const key = e.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

const SectionLabel = ({ children }) => (
  <div className="text-uppercase text-muted small font-weight-bold mb-2" style={{ letterSpacing: 0.5 }}>
    {children}
  </div>
)

const ContactRow = ({ contact, disabled, onToggle }) => {
  const included = !contact.isExcluded
  return (
    <div
      className="d-flex align-items-center px-3 py-2 border-bottom"
      style={{ background: included ? 'transparent' : '#f8f9fa', cursor: disabled ? 'not-allowed' : 'pointer' }}
      onClick={() => !disabled && onToggle(!contact.isExcluded ? true : false)}
    >
      <Input
        type="checkbox"
        className="m-0 mr-3"
        style={{ position: 'relative', marginTop: 0, transform: 'scale(1.2)' }}
        checked={included}
        disabled={disabled}
        onChange={(e) => onToggle(!e.target.checked)}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="flex-grow-1">
        <div style={{ fontWeight: 500, color: included ? 'inherit' : '#6c757d' }}>
          {contact.name || <span className="text-muted font-italic">No name on contact</span>}
        </div>
        <div className="small text-muted">
          {contact.email || <span className="font-italic">no email</span>}
        </div>
      </div>
      {!included && <Badge color="secondary">Excluded</Badge>}
    </div>
  )
}

const RecipientsModal = ({ isOpen, toggle, tenantId, isAdmin, includeInactive = false }) => {
  const dispatch = useDispatch()
  const tenant = useSelector((state) =>
    (state.cspRenewals.tenants || []).find((t) => t.tenantId === tenantId)
  )
  const updatingTenantId = useSelector((state) => state.cspRenewals.updatingTenantId)
  const isUpdating = updatingTenantId === tenantId

  const [overrideInput, setOverrideInput] = useState('')

  // Seed the override input when the modal opens or the underlying value changes,
  // so opening with no edits doesn't accidentally blank an existing override.
  useEffect(() => {
    if (isOpen) {
      setOverrideInput(tenant?.contactOverrideEmail ?? '')
    }
  }, [isOpen, tenant?.contactOverrideEmail])

  if (!tenant) return null

  const contacts = tenant.contacts || []
  const activeCount = contacts.filter((c) => !c.isExcluded).length
  const overrideCount = splitOverrideEmails(tenant.contactOverrideEmail).length
  const totalRecipients = activeCount + overrideCount
  const overrideChanged = overrideInput.trim() !== (tenant.contactOverrideEmail ?? '')

  const handleSaveOverride = () => {
    const trimmed = overrideInput.trim()
    dispatch(setContactOverride(tenantId, trimmed === '' ? null : trimmed, includeInactive))
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>
        Manage Recipients
        <div className="small text-muted mt-1" style={{ fontWeight: 400 }}>
          {tenant.companyName || tenant.tenantName}
        </div>
      </ModalHeader>
      <ModalBody>
        {totalRecipients === 0 && (
          <div className="alert alert-danger py-2 small mb-3">
            <strong>No recipients selected.</strong> Renewal notices for this
            tenant won't be sent until at least one contact is included or an
            override email is set.
          </div>
        )}

        <SectionLabel>Primary contacts from ConnectWise</SectionLabel>
        {contacts.length === 0 ? (
          <div className="border rounded px-3 py-3 mb-3 text-muted small">
            No primary contacts found in ConnectWise for this company. Add a
            contact with the &ldquo;Primary Contact&rdquo; type in CW, or set
            an override email below.
          </div>
        ) : (
          <div className="border rounded mb-3" style={{ overflow: 'hidden' }}>
            {contacts.map((c, i) => (
              <ContactRow
                key={c.cwContactId}
                contact={c}
                disabled={!isAdmin || isUpdating}
                onToggle={(isExcluded) => dispatch(setContactExcluded(tenantId, c.cwContactId, isExcluded, includeInactive))}
              />
            ))}
          </div>
        )}

        <SectionLabel>Additional recipients (optional)</SectionLabel>
        <div className="text-muted small mb-2">
          Sends to these addresses in addition to any included primary contacts
          above. Separate multiple addresses with commas or semicolons. Leave
          blank to use only the contacts above.
        </div>
        <FormGroup className="mb-0">
          <div className="d-flex align-items-center">
            <Input
              type="text"
              placeholder="alias@company.com, accounts@company.com"
              value={overrideInput}
              onChange={(e) => setOverrideInput(e.target.value)}
              disabled={!isAdmin || isUpdating}
              bsSize="sm"
              style={{ flex: 1 }}
            />
            <Button
              size="sm"
              color="outline-primary"
              className="ml-2"
              disabled={!isAdmin || isUpdating || !overrideChanged}
              onClick={handleSaveOverride}
            >
              Save
            </Button>
          </div>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <small className="text-muted mr-auto">
          {totalRecipients} recipient{totalRecipients === 1 ? '' : 's'}
        </small>
        <Button color="secondary" onClick={toggle}>Close</Button>
      </ModalFooter>
    </Modal>
  )
}

export default RecipientsModal
